/**
 * Shimmering placeholders shaped like the content they stand in for.
 *
 * A row of shimmer inside `.rw`/`.rowgrid` reads as "a client row is coming"
 * before any data has arrived, and — because it sits in the exact same grid
 * the real row will — nothing reflows the instant it's replaced. That's the
 * whole point over a single centred "Loading…" line: the reader's eye is
 * already on the right shape when the numbers land.
 */

/** One shimmering line, sized like a piece of text. */
export function SkeletonLine({ width = '100%', size = '' }) {
  return <span className={`shimmer shimmer-line ${size}`} style={{ width }} />
}

/**
 * One table row's worth of shimmer.
 *
 * `cols` is the same grid-template-columns string the real rows use, so the
 * placeholder lines up cell-for-cell with what replaces it. The first cell
 * gets two stacked lines (every one of these tables opens on a name plus a
 * code/id underneath); the rest get one line each, narrower so the row reads
 * as "not text yet" rather than as a fully redacted document.
 */
export function SkeletonRow({ cols, cells, count = 1 }) {
  return (
    <>
      {Array.from({ length: count }, (_, i) => (
        <div className="rw" key={i}>
          <div className="rowgrid" style={{ gridTemplateColumns: cols }}>
            <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <SkeletonLine width="72%" />
              <SkeletonLine width="38%" size="sm" />
            </span>
            {Array.from({ length: Math.max(0, cells - 1) }, (_, j) => (
              <SkeletonLine key={j} width={`${45 + ((i + j) % 4) * 8}%`} />
            ))}
          </div>
        </div>
      ))}
    </>
  )
}

/** A generic rectangular block — a folder card, a timeline entry, anything not row-shaped. */
export function SkeletonBlock({ height = 48, style = {} }) {
  return <div className="shimmer" style={{ height, ...style }} />
}
